import React from 'react';
import { useTranslations } from 'next-intl';
import { APP_EVENTS, logHubxEvent } from '@/app/providers/analytics';
import { formatAnalyticsSource } from '@/helpers/common';
import { buildStructuredAnalyticsSource } from '@/lib/analytics/utils/source';
import ProtectPdfDialog from './ProtectPdfDialog';
import ProtectPdfField from './ProtectPdfField';
import ProtectPdfButton from './ProtectPdfButton';

export type ProtectPdfPasswordModalData = {
  toolType?: string;
  originEntryPoint?: string;
  downloadVariant?: number;
  onProtect: (password: string) => Promise<boolean>;
  onDismiss: () => void;
};

const ProtectPdfPasswordModal = ({
  data,
  hideModal,
}: {
  data?: ProtectPdfPasswordModalData;
  hideModal: () => void;
}) => {
  const t = useTranslations('protect_pdf_flow');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [isConfirmTouched, setIsConfirmTouched] = React.useState(false);
  const [isSubmitAttempted, setIsSubmitAttempted] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const isMountedRef = React.useRef(true);
  const isSubmittingRef = React.useRef(false);

  React.useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    const formattedToolType = formatAnalyticsSource(data?.toolType);

    logHubxEvent({
      eventName: APP_EVENTS.LND_DOWNLOAD_POPUP,
      source: formattedToolType,
      event_params: {
        source: buildStructuredAnalyticsSource(
          formattedToolType,
          data?.originEntryPoint
        ),
        ...(typeof data?.downloadVariant === 'number'
          ? { downloadVariant: data.downloadVariant }
          : {}),
      },
    });
  }, [data?.downloadVariant, data?.originEntryPoint, data?.toolType]);

  const passwordsMatch = password === confirmPassword;
  const canSubmit =
    password.length > 0 && confirmPassword.length > 0 && passwordsMatch;
  const shouldShowMismatch =
    password.length > 0 &&
    confirmPassword.length > 0 &&
    !passwordsMatch &&
    (isConfirmTouched ||
      isSubmitAttempted ||
      confirmPassword.length >= password.length);

  const passwordError =
    isSubmitAttempted && password.length === 0
      ? t('empty_password_error')
      : null;
  const confirmPasswordError = shouldShowMismatch
    ? t('mismatch_error')
    : isSubmitAttempted && password.length > 0 && confirmPassword.length === 0
      ? t('empty_password_error')
      : null;

  const handleDismiss = React.useCallback(() => {
    if (isSubmittingRef.current) {
      return;
    }

    data?.onDismiss();
    hideModal();
  }, [data, hideModal]);

  const submit = React.useCallback(async () => {
    setIsSubmitAttempted(true);

    if (!canSubmit || isSubmittingRef.current || !data) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const isProtected = await data.onProtect(password);

    isSubmittingRef.current = false;

    if (!isProtected && isMountedRef.current) {
      setIsSubmitting(false);
    }
  }, [canSubmit, data, password]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submit();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (
      event.key !== 'Enter' ||
      event.nativeEvent.isComposing ||
      !(event.target instanceof HTMLInputElement)
    ) {
      return;
    }

    event.preventDefault();
    void submit();
  };

  return (
    <ProtectPdfDialog
      name="ProtectPdfPassword"
      tone="lock"
      title={t('protect_title')}
      description={t('protect_description')}
      onDismiss={handleDismiss}
      showCloseIcon
      isDismissable={false}
    >
      <form
        noValidate
        onSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
        className="flex flex-col gap-8"
      >
        <div className="flex flex-col gap-6">
          <ProtectPdfField
            id="protect-pdf-password"
            label={t('password_label')}
            placeholder={t('password_placeholder')}
            value={password}
            onChange={setPassword}
            autoFocus
            showStrength
            errorMessage={passwordError}
            disabled={isSubmitting}
          />
          <ProtectPdfField
            id="protect-pdf-confirm-password"
            label={t('confirm_password_label')}
            placeholder={t('confirm_password_placeholder')}
            value={confirmPassword}
            onChange={setConfirmPassword}
            onBlur={() => setIsConfirmTouched(true)}
            errorMessage={confirmPasswordError}
            disabled={isSubmitting}
          />
        </div>

        <ProtectPdfButton
          type="submit"
          isDisabled={!canSubmit}
          isLoading={isSubmitting}
        >
          {t('protect_button')}
        </ProtectPdfButton>
      </form>
    </ProtectPdfDialog>
  );
};

export default ProtectPdfPasswordModal;
